import React, { useState, useEffect, useCallback } from 'react';
import { FaFacebook, FaTwitter, FaLinkedin, FaInstagram, FaImages, FaGithub, FaYoutube } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import '../styles/Footer.scss';
import { newsletterAPI, cmsAPI, getImageUrl } from '../utils/api';

function Footer() {
  const [emailInput, setEmailInput] = useState('');
  const [subscribeMessage, setSubscribeMessage] = useState('');
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [services, setServices] = useState([]);
  const location = useLocation();

  // Watch for ?openGallery=true in URL to trigger footer gallery modal
  useEffect(() => {
    if (location.search.includes('openGallery=true')) {
      setIsGalleryOpen(true);
      // Clean query parameter from address bar
      window.history.replaceState(null, '', location.pathname);
    }
  }, [location]);

  // Fetch social links and gallery
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [socialRes, galleryRes] = await Promise.all([
          cmsAPI.getSocialLinks(),
          cmsAPI.getGallery()
        ]);
        if (socialRes.data.success) setSocialLinks(socialRes.data.data);

        let dbGallery = [];
        if (galleryRes.data?.success && galleryRes.data?.data) {
          dbGallery = galleryRes.data.data;
        }

        // List of all actual website images (Projects, Services, Collaborations, Team, Banner)
        const websiteImages = [
          { id: 'web-1', title: 'Agritech Platform', image_url: '/images/port-agritech.jpg' },
          { id: 'web-2', title: 'Dairy Management System', image_url: '/images/port-dairy.jpg' },
          { id: 'web-3', title: 'Healthcare Portal', image_url: '/images/port-healthcare.jpg' },
          { id: 'web-4', title: 'IoT Smart Automation', image_url: '/images/port-iot.jpg' },
          { id: 'web-5', title: 'Cloud Solutions', image_url: '/images/svc-cloud.jpg' },
          { id: 'web-6', title: 'E-Commerce Platform', image_url: '/images/svc-ecommerce.jpg' },
          { id: 'web-7', title: 'Education & Admission', image_url: '/images/svc-education.jpg' },
          { id: 'web-8', title: 'Digital Marketing', image_url: '/images/svc-marketing.jpg' },
          { id: 'web-9', title: 'Mobile App Development', image_url: '/images/svc-mobile.jpg' },
          { id: 'web-10', title: 'Documentation & Research', image_url: '/images/svc-research.jpg' },
          { id: 'web-11', title: 'UI/UX Design', image_url: '/images/svc-uiux.jpg' },
          { id: 'web-12', title: 'Web Development', image_url: '/images/svc-web.jpg' },
          { id: 'web-13', title: 'Collaborations & Partnerships', image_url: '/images/collab-hero.jpg' },
          { id: 'web-14', title: 'CSR Initiatives', image_url: '/images/collab-csr.jpg' },
          { id: 'web-15', title: 'Medhavi Skills University', image_url: '/images/collab-medhavi.jpg' },
          { id: 'web-16', title: 'Our Dynamic Team', image_url: '/images/about-team.jpg' },
          { id: 'web-17', title: 'Digital Innovation', image_url: '/images/about-bg.jpg' },
          { id: 'web-18', title: 'Empowering Businesses', image_url: '/images/hero-bg.jpg' },
          { id: 'web-19', title: 'Nishant Kale - CEO', image_url: '/images/team-ceo.jpg' },
          { id: 'web-20', title: 'Vaishnavi Kale - CTO', image_url: '/images/team-cto.jpg' },
          { id: 'web-21', title: 'Ganesh Bhujbal - Associate', image_url: '/images/team-design.jpg' },
          { id: 'web-22', title: 'Prasad Kawade - PM', image_url: '/images/team-pm.jpg' },
        ];

        // Filter out any website images that might already be in the database to avoid duplicates
        const existingUrls = new Set(dbGallery.map(item => item.image_url));
        const filteredWebsiteImages = websiteImages.filter(item => !existingUrls.has(item.image_url));

        // Combine database gallery items (new additions) with the actual website images
        setGalleryItems([...dbGallery, ...filteredWebsiteImages]);
        const servicesRes = await cmsAPI.getServices();
        if (servicesRes.data.success) setServices(servicesRes.data.data);
      } catch (error) {
        console.error('Error fetching footer data:', error);
      }
    };
    fetchData();
  }, []);

  // Map icon strings to components
  const getIcon = (iconName) => {
    const icons = {
      FaFacebook: <FaFacebook size={20} />,
      FaTwitter: <FaTwitter size={20} />,
      FaLinkedin: <FaLinkedin size={20} />,
      FaInstagram: <FaInstagram size={20} />,
      FaGithub: <FaGithub size={20} />,
      FaYoutube: <FaYoutube size={20} />,
    };
    return icons[iconName] || <FaFacebook size={20} />;
  };

  // Handle next/prev image
  const handlePrevImage = useCallback((e) => {
    if (e) e.stopPropagation();
    setSelectedImageIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
  }, [galleryItems.length]);

  const handleNextImage = useCallback((e) => {
    if (e) e.stopPropagation();
    setSelectedImageIndex((prev) => (prev === galleryItems.length - 1 ? 0 : prev + 1));
  }, [galleryItems.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex !== null) {
        if (e.key === 'Escape') setSelectedImageIndex(null);
        if (e.key === 'ArrowLeft') handlePrevImage();
        if (e.key === 'ArrowRight') handleNextImage();
      } else if (isGalleryOpen && e.key === 'Escape') {
        setIsGalleryOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryOpen, selectedImageIndex, handlePrevImage, handleNextImage]);

  // Validate email format
  const isValidEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!emailInput) {
      setSubscribeMessage('Please enter a valid email');
      setSubscribeSuccess(false);
      return;
    }

    if (!isValidEmail(emailInput)) {
      setSubscribeMessage('❌ Please enter a valid email address');
      setSubscribeSuccess(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await newsletterAPI.subscribe(emailInput);
      // axios wraps response in .data property
      const data = response.data || response;
      setSubscribeMessage(`✅ ${data.message}`);
      setSubscribeSuccess(true);
      setEmailInput('');
      setTimeout(() => {
        setSubscribeMessage('');
        setSubscribeSuccess(false);
      }, 3000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Subscription failed. Please try again.';
      setSubscribeMessage(`❌ ${errorMsg}`);
      setSubscribeSuccess(false);
      console.error('Subscription error:', error);
    } finally {
      setIsLoading(false);
    }
  };





  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          {/* Brand Section */}
          <div className="footer-brand">
            <div className="footer-logo">
              <img src="/images/logo.png" alt="Pravara Infotech" />
            </div>
            <p className="footer-brand-desc">Pravara Infotech is a leading technology company dedicated to empowering businesses with innovative digital solutions. We combine creativity, technical expertise, and strategic thinking to deliver results that matter.</p>
            <div className="footer-social">
              {socialLinks.map((link) => (
                <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" aria-label={link.platform} title={link.platform} className="social-icon">
                  {getIcon(link.icon)}
                </a>
              ))}

              <button
                onClick={() => setIsGalleryOpen(true)}
                aria-label="Gallery"
                title="Gallery"
                className="social-icon gallery-icon-btn"
              >
                <FaImages size={20} />
              </button>
            </div>
          </div>

          {/* Dynamic Services */}
          <div className="footer-col">
            <h4>Our Services</h4>
            <ul>
              {services.length > 0 ? services.slice(0, 4).map(s => (
                <li key={s.id}><a href="/services">{s.title}</a></li>
              )) : (
                <>
                  <li><a href="/services">Web Development</a></li>
                  <li><a href="/services">Mobile App Development</a></li>
                  <li><a href="/services">UI/UX Design</a></li>
                  <li><a href="/services">Cloud Solutions</a></li>
                </>
              )}
            </ul>
          </div>

          <div className="footer-col">
            <h4>More Services</h4>
            <ul>
              {services.length > 4 ? services.slice(4, 8).map(s => (
                <li key={s.id}><a href="/services">{s.title}</a></li>
              )) : (
                <>
                  <li><a href="/services">Digital Marketing</a></li>
                  <li><a href="/services">Education & Admission</a></li>
                  <li><a href="/services">Documentation & Research</a></li>
                </>
              )}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="footer-newsletter">
            <h4>Stay Updated</h4>
            <p>Subscribe to our newsletter for the latest updates, insights, and technology trends delivered straight to your inbox.</p>
            <form onSubmit={handleSubscribe} className="newsletter-form">
              <input
                type="email"
                placeholder="Your email id here"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                disabled={isLoading}
                required
                pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                title="Please enter a valid email address"
                aria-label="Email address"
              />
              <button type="submit" disabled={isLoading}>
                {isLoading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {subscribeMessage && <p className={`subscribe-msg ${subscribeSuccess ? 'success' : 'error'}`}>{subscribeMessage}</p>}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <p>&copy; 2026 Pravara Infotech</p>
          <div className="footer-links">
            <a href="/">Home</a>
            <a href="/about">About Us</a>
            <a href="/services">Services</a>
            <a href="/collaborations">Collaborations</a>
            <a href="/contact">Contact</a>
            <a href="/career">Careers</a>
            <a href="/admin">Admin</a>
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      {isGalleryOpen && (
        <div className="gallery-modal-overlay" onClick={() => setIsGalleryOpen(false)}>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-modal-close" onClick={() => setIsGalleryOpen(false)}>
              &times;
            </button>
            <h3 className="gallery-modal-title">Our Gallery</h3>
            <div className="gallery-modal-grid">
              {galleryItems.length > 0 ? galleryItems.map((item, index) => (
                <div key={item.id} className="gallery-modal-item" onClick={() => setSelectedImageIndex(index)}>
                  <img src={getImageUrl(item.image_url)} alt={item.title || `Gallery ${index}`} />
                </div>
              )) : (
                <div className="no-images">
                  <p>No images in gallery yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Lightbox */}
      {selectedImageIndex !== null && galleryItems.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setSelectedImageIndex(null)}>
          <button className="lightbox-close" onClick={() => setSelectedImageIndex(null)}>
            &times;
          </button>

          <button className="lightbox-arrow left" onClick={handlePrevImage}>
            &#10094;
          </button>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(galleryItems[selectedImageIndex].image_url)}
              alt={galleryItems[selectedImageIndex].title || `Gallery ${selectedImageIndex}`}
            />
          </div>

          <button className="lightbox-arrow right" onClick={handleNextImage}>
            &#10095;
          </button>
        </div>
      )}
    </footer>
  );
}

export default Footer;
